import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { ReactSelect } from "@/components/ui/react-select";
import { toast } from "sonner";
import TemplatePreviewModal from "../templatePreviewModal";
import {
  ASSIGNABLE_TEMPLATE_GROUPS,
  ASSIGNABLE_TEMPLATE_TYPES,
  invalidateAllPrinterQueries,
} from "./printerConstants";
import {
  Layers,
  Sparkles,
  Eye,
  Search,
  Check,
  Printer,
  Ruler,
  AlertCircle,
  X,
  Plus,
  Trash2,
  CheckCircle2,
  FileText,
  Receipt,
  Tag,
  Package,
  BarChart3,
  Wrench,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Helper to choose clean contextual document icon
const getDocIcon = (group) => {
  switch (group) {
    case "Sales & Receipts":
      return Receipt;
    case "Barcodes & Labels":
      return Tag;
    case "Purchasing & Inventory":
      return Package;
    case "Reports":
      return BarChart3;
    case "Repairs & Service":
      return Wrench;
    default:
      return FileText;
  }
};

export default function PaperSizeTemplateAssignmentsModal({
  paperSize,
  printer,
  open,
  onClose,
}) {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (p) => {
    if (!permissions) return false;
    if (Array.isArray(p)) return p.some((perm) => permissions.includes(perm));
    return permissions.includes(p);
  };

  const canEdit = hasPermission("printer update");
  const canRead = hasPermission("printer read");

  const paperSizeId = paperSize?.id;
  const storeId = printer?.store_id || paperSize?.store_id || currentStore?.id;

  // Assignments & Filter State
  const [assignmentsState, setAssignmentsState] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [viewTab, setViewTab] = useState("assigned"); // 'assigned' | 'recommended' | 'all'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Quick Add State (for assigning a new document easily from the Assigned tab)
  const [quickAddDocType, setQuickAddDocType] = useState(null);
  const [quickAddTemplate, setQuickAddTemplate] = useState(null);

  // Query existing assignments
  const {
    data: assignmentsData,
    isLoading: assignmentsLoading,
  } = useQuery({
    queryKey: ["paper-size-templates", paperSizeId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.PAPER_SIZES.TEMPLATES(paperSizeId)
      );
      return res.data?.data ?? res.data ?? [];
    },
    enabled: Boolean(paperSizeId && open && canRead),
  });

  // Query available templates library for store
  const { data: allTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["templates", storeId],
    queryFn: async () => {
      const params = {};
      if (storeId) params.store_id = storeId;
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.TEMPLATES, {
        params,
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: Boolean(open && hasPermission("template read")),
  });

  // Target archetype for this paper size
  const targetArchetype = useMemo(() => {
    const isThermal =
      paperSize?.size === "thermal_80" ||
      paperSize?.size === "thermal_58" ||
      (paperSize?.size === "custom" && Number(paperSize?.height) === 0);

    const isLabel =
      paperSize?.size === "custom" &&
      Number(paperSize?.height) > 0 &&
      Number(paperSize?.height) <= 50;

    if (isThermal) return "pos_thermal";
    if (isLabel) return "label_barcode";
    return "office_laser";
  }, [paperSize?.size, paperSize?.height]);

  // Recommended document types for this paper tray
  const recommendedTypes = useMemo(() => {
    return ASSIGNABLE_TEMPLATE_TYPES.filter(
      (t) => t.recommendedTarget === targetArchetype
    );
  }, [targetArchetype]);

  // Initialize state when modal opens and assignments load
  useEffect(() => {
    if (!open || assignmentsLoading) return;

    const state = {};
    let count = 0;
    if (Array.isArray(assignmentsData)) {
      assignmentsData.forEach((a) => {
        if (a && a.template_type && a.template_content_id) {
          state[a.template_type] = {
            id: a.id,
            printer_paper_size_id: a.printer_paper_size_id,
            template_type: a.template_type,
            template_type_label: a.template_type_label,
            template_content_id: a.template_content_id,
            template_content: a.template_content || null,
            is_active: a.is_active !== false,
            meta: Array.isArray(a.meta) ? a.meta : null,
          };
          count++;
        }
      });
    }
    setAssignmentsState(state);

    // Stable snapshot of assignments for isDirty comparison
    const snap = {};
    Object.keys(state).sort().forEach((k) => {
      snap[k] = {
        template_content_id: state[k].template_content_id,
        is_active: state[k].is_active,
      };
    });
    setInitialSnapshot(JSON.stringify(snap));

    // Default to 'assigned' if assignments exist, otherwise 'recommended'
    setViewTab(count > 0 ? "assigned" : "recommended");
  }, [open, assignmentsLoading, assignmentsData]);

  const isDirty = useMemo(() => {
    const snap = {};
    Object.keys(assignmentsState).sort().forEach((k) => {
      if (assignmentsState[k]?.template_content_id) {
        snap[k] = {
          template_content_id: assignmentsState[k].template_content_id,
          is_active: assignmentsState[k].is_active,
        };
      }
    });
    return JSON.stringify(snap) !== initialSnapshot;
  }, [assignmentsState, initialSnapshot]);

  const assignedCount = useMemo(() => {
    return Object.values(assignmentsState).filter(
      (item) => item && item.template_content_id
    ).length;
  }, [assignmentsState]);

  // Bulk Upsert Mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!canEdit) {
        throw new Error("You do not have permission to update paper size templates");
      }
      const assignmentsList = Object.entries(assignmentsState)
        .filter(([, item]) => item && item.template_content_id)
        .map(([template_type, item]) => ({
          template_type,
          template_content_id: item.template_content_id,
          is_active: Boolean(item.is_active),
          meta: Array.isArray(item.meta) && item.meta.length > 0 ? item.meta : null,
        }));

      const payload = {
        assignments: assignmentsList,
      };

      const res = await apiClient.put(
        API_ENDPOINTS.PAPER_SIZES.TEMPLATES(paperSizeId),
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success("Document template assignments saved successfully!");
      invalidateAllPrinterQueries(queryClient);
      onClose();
    },
    onError: (err) => {
      const errors = err?.response?.data?.errors;
      let errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save template assignments";
      if (errors && typeof errors === "object") {
        const errorDetails = Object.values(errors).flat().join(", ");
        if (errorDetails) errorMsg = `${errorMsg}: ${errorDetails}`;
      }
      toast.error(errorMsg);
    },
  });

  const handleSelectTemplate = (typeValue, selectedId) => {
    setAssignmentsState((prev) => {
      const next = { ...prev };
      if (!selectedId || selectedId === "__none__") {
        delete next[typeValue];
      } else {
        const chosenTemplate = allTemplates.find((t) => t.id === selectedId);
        next[typeValue] = {
          ...prev[typeValue],
          template_type: typeValue,
          template_content_id: selectedId,
          template_content: chosenTemplate || prev[typeValue]?.template_content || null,
          is_active: prev[typeValue]?.is_active ?? true,
          meta: prev[typeValue]?.meta ?? null,
        };
      }
      return next;
    });
  };

  const handleToggleActive = (typeValue) => {
    setAssignmentsState((prev) => {
      const current = prev[typeValue];
      if (!current) return prev;
      return {
        ...prev,
        [typeValue]: {
          ...current,
          is_active: !current.is_active,
        },
      };
    });
  };

  const handleClearType = (typeValue) => {
    setAssignmentsState((prev) => {
      const next = { ...prev };
      delete next[typeValue];
      return next;
    });
  };

  // Smart Auto-Assign based on Paper Size
  const handleAutoMatch = () => {
    const isThermal = targetArchetype === "pos_thermal";
    const next = { ...assignmentsState };
    let matchedCount = 0;

    ASSIGNABLE_TEMPLATE_TYPES.forEach((typeObj) => {
      const isPriority = typeObj.recommendedTarget === targetArchetype;

      if (!isPriority && isThermal && typeObj.value !== "sales_receipt") {
        return; // Don't assign reports or invoices to thermal receipt roll
      }

      const matching = allTemplates.filter(
        (t) => t.type === typeObj.value && t.is_active !== false
      );
      if (matching.length === 0) return;

      const storeTemplate = matching.find((t) => t.store_id && !t.is_system);
      const systemTemplate = matching.find((t) => t.is_system);
      const sharedTemplate = matching.find((t) => !t.is_system && !t.store_id);
      const chosen = storeTemplate || systemTemplate || sharedTemplate || matching[0];

      if (chosen) {
        next[typeObj.value] = {
          template_type: typeObj.value,
          template_type_label: typeObj.label,
          template_content_id: chosen.id,
          template_content: chosen,
          is_active: true,
          meta: null,
        };
        matchedCount++;
      }
    });

    setAssignmentsState(next);
    setViewTab("assigned");
    toast.success(
      `Smart auto-matched ${matchedCount} recommended template${matchedCount === 1 ? "" : "s"
      }! Click "Save Assignments" to apply.`
    );
  };

  const handlePreview = (templateId, typeValue) => {
    if (!hasPermission("template read")) return;
    const fromState = assignmentsState[typeValue]?.template_content;
    const fromAll = allTemplates.find((t) => t.id === templateId);
    const chosen = fromAll || fromState;

    if (chosen) {
      setPreviewTemplate(chosen);
    } else {
      setPreviewTemplate({
        id: templateId,
        name: assignmentsState[typeValue]?.template_type_label || `${typeValue} Template`,
        type: typeValue,
      });
    }
  };

  // Unassigned types for Quick Add
  const unassignedTypes = useMemo(() => {
    return ASSIGNABLE_TEMPLATE_TYPES.filter(
      (t) => !assignmentsState[t.value]?.template_content_id
    );
  }, [assignmentsState]);

  const quickAddDocOptions = useMemo(() => {
    return unassignedTypes.map((t) => ({
      value: t.value,
      label: `${t.label} (${t.group})`,
      docObj: t,
    }));
  }, [unassignedTypes]);

  const quickAddTemplateOptions = useMemo(() => {
    if (!quickAddDocType) return [];
    const matching = allTemplates.filter(
      (t) => t.type === quickAddDocType.value && t.is_active !== false
    );
    return matching.map((t) => {
      const tag = t.store_id ? " [Store]" : t.is_system ? " [System]" : " [Shared]";
      return {
        value: t.id,
        label: `${t.name}${tag}`,
        template: t,
      };
    });
  }, [allTemplates, quickAddDocType]);

  const handleAddQuickRoute = () => {
    if (!quickAddDocType || !quickAddTemplate) return;
    handleSelectTemplate(quickAddDocType.value, quickAddTemplate.value);
    setQuickAddDocType(null);
    setQuickAddTemplate(null);
    toast.success(`Added ${quickAddDocType.label} to assignments!`);
  };

  // Filtered types according to selected View Tab & Filters
  const displayedTypes = useMemo(() => {
    let list = ASSIGNABLE_TEMPLATE_TYPES;

    // View scope filter
    if (viewTab === "assigned") {
      list = list.filter((t) =>
        Boolean(assignmentsState[t.value]?.template_content_id)
      );
    } else if (viewTab === "recommended") {
      list = recommendedTypes;
    }

    // Category filter (active in 'all' tab)
    if (viewTab === "all" && selectedGroup !== "all") {
      const foundGroup = ASSIGNABLE_TEMPLATE_GROUPS.find(
        (g) => g.id === selectedGroup
      );
      if (foundGroup) {
        list = list.filter((item) => item.group === foundGroup.groupName);
      }
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((item) => {
        const matchesLabel = item.label.toLowerCase().includes(q);
        const matchesDesc = item.description?.toLowerCase().includes(q);
        return matchesLabel || matchesDesc;
      });
    }

    return list;
  }, [
    viewTab,
    assignmentsState,
    recommendedTypes,
    selectedGroup,
    searchQuery,
  ]);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-background">
          {/* Header */}
          <DialogHeader className="px-6 py-4 border-b border-border bg-card shrink-0 pr-12">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <DialogTitle className="text-base font-semibold flex items-center gap-2">
                    <span>Document Routing & Template Assignments</span>
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1 font-medium text-foreground">
                      <Printer className="h-3 w-3 text-muted-foreground" />
                      {printer?.name || "Printer"}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Ruler className="h-3 w-3" />
                      {paperSize?.label || "Paper Size"} ({paperSize?.size?.toUpperCase()})
                    </span>
                  </DialogDescription>
                </div>
              </div>

              <div className="flex items-center gap-2 self-start sm:self-auto">
                {!canEdit && (
                  <Badge
                    variant="outline"
                    className="text-amber-600 border-amber-500/30 bg-amber-500/10 text-xs"
                  >
                    View Only
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs font-normal py-1 px-2.5 bg-muted/40">
                  <span className="font-semibold text-foreground mr-1">{assignedCount}</span>{" "}
                  assigned
                </Badge>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAutoMatch}
                    className="h-8 gap-1.5 text-xs text-primary hover:bg-primary/10 hover:text-primary font-medium"
                    title="Automatically assigns recommended templates for this paper size"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Auto-Assign Recommended
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* View Scoping Tabs & Search Bar */}
          <div className="px-6 py-2.5 border-b border-border bg-muted/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
            {/* View Filter Segmented Controls */}
            <div className="flex items-center gap-1 p-0.5 bg-muted/60 rounded-lg border border-border/60">
              <button
                type="button"
                onClick={() => setViewTab("assigned")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer",
                  viewTab === "assigned"
                    ? "bg-background text-foreground shadow-xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                <span>Assigned</span>
                <Badge
                  variant="secondary"
                  className="text-[10px] py-0 px-1.5 h-4 ml-0.5 font-semibold"
                >
                  {assignedCount}
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => setViewTab("recommended")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer",
                  viewTab === "recommended"
                    ? "bg-background text-foreground shadow-xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                <span>Recommended</span>
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 ml-0.5">
                  {recommendedTypes.length}
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => setViewTab("all")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer",
                  viewTab === "all"
                    ? "bg-background text-foreground shadow-xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>All Types</span>
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 ml-0.5">
                  {ASSIGNABLE_TEMPLATE_TYPES.length}
                </Badge>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-60">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search document types…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs bg-background"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills (Shown only when browsing 'All Types') */}
          {viewTab === "all" && (
            <div className="px-6 py-2 border-b border-border/60 bg-muted/10 flex items-center gap-1.5 overflow-x-auto scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden shrink-0">
              {ASSIGNABLE_TEMPLATE_GROUPS.map((grp) => {
                const isSelected = selectedGroup === grp.id;
                return (
                  <button
                    key={grp.id}
                    type="button"
                    onClick={() => setSelectedGroup(grp.id)}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer",
                      isSelected
                        ? "bg-primary text-primary-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    {grp.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Document Types List */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {assignmentsLoading || templatesLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                <Spinner className="h-6 w-6" />
                <span className="text-xs">Loading template configurations…</span>
              </div>
            ) : viewTab === "assigned" && assignedCount === 0 ? (
              /* Friendly Empty State for Assigned Tab */
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-2 border-dashed border-border/70 rounded-2xl bg-muted/10 max-w-lg mx-auto my-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-3">
                  <Layers className="h-6 w-6" />
                </div>
                <h4 className="text-sm font-semibold text-foreground">
                  No documents routed to this paper size yet
                </h4>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Documents assigned here will automatically format and print to this tray.
                  Click Auto-Assign to configure recommended defaults in 1 click.
                </p>
                <div className="flex items-center gap-2 mt-5">
                  {canEdit && (
                    <Button
                      size="sm"
                      onClick={handleAutoMatch}
                      className="gap-1.5 text-xs shadow-xs"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Auto-Assign Recommended
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewTab("all")}
                    className="text-xs"
                  >
                    Browse All Documents
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Quick Add Bar on Assigned Tab (Allows adding remaining unassigned documents without switching tabs) */}
                {viewTab === "assigned" && unassignedTypes.length > 0 && canEdit && (
                  <div className="p-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
                    <span className="text-xs font-semibold text-primary flex items-center gap-1.5 shrink-0">
                      <Plus className="h-4 w-4" />
                      Route Document:
                    </span>
                    <div className="flex-1 min-w-[200px]">
                      <ReactSelect
                        options={quickAddDocOptions}
                        value={quickAddDocType}
                        onChange={(opt) => {
                          setQuickAddDocType(opt);
                          setQuickAddTemplate(null);
                        }}
                        placeholder="Choose document type…"
                        className="text-xs"
                      />
                    </div>
                    {quickAddDocType && (
                      <div className="flex-1 min-w-[200px]">
                        <ReactSelect
                          options={quickAddTemplateOptions}
                          value={quickAddTemplate}
                          onChange={(opt) => setQuickAddTemplate(opt)}
                          placeholder="Select template…"
                          className="text-xs"
                        />
                      </div>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddQuickRoute}
                      disabled={!quickAddDocType || !quickAddTemplate}
                      className="h-8 text-xs shrink-0"
                    >
                      Add Route
                    </Button>
                  </div>
                )}

                {/* Rendered Document List */}
                {displayedTypes.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-xs">
                    No document types match your current filter or search query.
                  </div>
                ) : (
                  displayedTypes.map((typeObj) => {
                    const assigned = assignmentsState[typeObj.value];
                    const isAssigned = Boolean(assigned && assigned.template_content_id);
                    const isActive = isAssigned && assigned.is_active;
                    const DocIcon = getDocIcon(typeObj.group);

                    // Templates for this specific document type
                    const matchingTemplates = allTemplates.filter(
                      (t) => t.type === typeObj.value && t.is_active !== false
                    );

                    const selectOptions = [
                      { value: "__none__", label: "- Remove Assignment (Use Fallback) -" },
                      ...matchingTemplates.map((t) => {
                        const tag = t.store_id
                          ? " [Store]"
                          : t.is_system
                            ? " [System]"
                            : " [Shared]";
                        return {
                          value: t.id,
                          label: `${t.name}${tag}`,
                          template: t,
                        };
                      }),
                    ];

                    // Ensure the assigned template appears in selectOptions even if allTemplates is missing it
                    if (isAssigned && assigned.template_content_id) {
                      const exists = selectOptions.some(
                        (o) => o.value === assigned.template_content_id
                      );
                      if (!exists) {
                        const tName =
                          assigned.template_content?.name ||
                          assigned.template_type_label ||
                          "Assigned Template";
                        const tag = assigned.template_content?.is_system
                          ? " [System]"
                          : "";
                        selectOptions.push({
                          value: assigned.template_content_id,
                          label: `${tName}${tag}`,
                          template: assigned.template_content,
                        });
                      }
                    }

                    const currentSelectValue = isAssigned
                      ? selectOptions.find(
                        (o) => o.value === assigned.template_content_id
                      ) || {
                        value: assigned.template_content_id,
                        label:
                          assigned.template_content?.name ||
                          assigned.template_type_label ||
                          "Assigned Template",
                      }
                      : null;

                    /* ── 1. Assigned Document Card (Clean, Prominent & Focused) ── */
                    if (isAssigned) {
                      return (
                        <div
                          key={typeObj.value}
                          className={cn(
                            "p-3.5 rounded-xl border transition-all duration-150 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs",
                            isActive
                              ? "border-primary/30 bg-card hover:border-primary/50"
                              : "border-border/60 bg-muted/15 opacity-80"
                          )}
                        >
                          {/* Left: Document Info with Clean Icon */}
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                              <DocIcon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-foreground">
                                  {typeObj.label}
                                </span>
                                {assigned.template_type_label &&
                                  assigned.template_type_label !== typeObj.label && (
                                    <span className="text-[10px] text-muted-foreground font-normal">
                                      ({assigned.template_type_label})
                                    </span>
                                  )}
                                <Badge
                                  variant={isActive ? "default" : "secondary"}
                                  className="text-[10px] py-0 px-1.5 h-4 font-normal"
                                >
                                  {isActive ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                              {assigned.template_content?.name ? (
                                <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                                  Template: <span className="font-medium text-foreground">{assigned.template_content.name}</span>
                                </p>
                              ) : (
                                <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                                  {typeObj.description}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Right: Template Selector & Actions */}
                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto w-full sm:w-auto justify-between sm:justify-start">
                            {canEdit ? (
                              <div className="w-full sm:w-64">
                                <ReactSelect
                                  options={selectOptions}
                                  value={currentSelectValue}
                                  onChange={(opt) =>
                                    handleSelectTemplate(typeObj.value, opt?.value)
                                  }
                                  isDisabled={!canEdit}
                                  isSearchable={selectOptions.length > 5}
                                  className="text-xs"
                                  placeholder="Select template…"
                                />
                              </div>
                            ) : (
                              <div
                                className="text-xs font-medium text-foreground px-2.5 py-1 rounded-md bg-muted/40 border border-border/60 max-w-[220px] truncate"
                                title={currentSelectValue?.label}
                              >
                                {currentSelectValue?.label || "Assigned"}
                              </div>
                            )}

                            {/* Preview Button */}
                            {hasPermission("template read") && assigned.template_content_id && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                                onClick={() =>
                                  handlePreview(
                                    assigned.template_content_id,
                                    typeObj.value
                                  )
                                }
                                title="Preview assigned template"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}

                            {/* Active Toggle Switch */}
                            {canEdit && (
                              <div className="flex items-center gap-1.5 pl-1 shrink-0">
                                <Switch
                                  checked={Boolean(assigned.is_active)}
                                  onCheckedChange={() =>
                                    handleToggleActive(typeObj.value)
                                  }
                                  title={
                                    assigned.is_active
                                      ? "Click to deactivate assignment"
                                      : "Click to activate assignment"
                                  }
                                />
                              </div>
                            )}

                            {/* Clear Button */}
                            {canEdit && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
                                onClick={() => handleClearType(typeObj.value)}
                                title="Remove assignment (revert to fallback)"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    }

                    /* ── 2. Unassigned Document Row (Minimal & Uncluttered) ── */
                    return (
                      <div
                        key={typeObj.value}
                        className="px-3.5 py-2.5 rounded-xl border border-border/40 bg-muted/5 hover:border-border/80 hover:bg-card/60 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground shrink-0">
                            <DocIcon className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-xs font-medium text-foreground">
                              {typeObj.label}
                            </span>
                            <p className="text-[11px] text-muted-foreground truncate">
                              {typeObj.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                          {canEdit ? (
                            matchingTemplates.length > 0 ? (
                              <div className="w-48 sm:w-56">
                                <ReactSelect
                                  options={[
                                    { value: "", label: "+ Assign Template…" },
                                    ...selectOptions.filter((o) => o.value !== "__none__"),
                                  ]}
                                  value={{ value: "", label: "+ Assign Template…" }}
                                  onChange={(opt) => {
                                    if (opt?.value) {
                                      handleSelectTemplate(typeObj.value, opt.value);
                                    }
                                  }}
                                  isSearchable={matchingTemplates.length > 5}
                                  className="text-xs"
                                  placeholder="+ Assign Template…"
                                />
                              </div>
                            ) : (
                              <span className="text-[11px] text-muted-foreground italic px-2">
                                No templates created
                              </span>
                            )
                          ) : (
                            <span className="text-[11px] text-muted-foreground px-2">
                              Not assigned
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-3 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-between shrink-0">
            {canEdit ? (
              <>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={saveMutation.isPending}
                  >
                    Cancel
                  </Button>
                  {isDirty && (
                    <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Unsaved changes
                    </span>
                  )}
                </div>

                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || !canEdit || !isDirty}
                  className="gap-1.5"
                >
                  {saveMutation.isPending ? (
                    <>
                      <Spinner className="h-3.5 w-3.5" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      Save Assignments
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <span className="text-xs text-muted-foreground">
                  View-only mode (printer update permission required to modify)
                </span>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          open={Boolean(previewTemplate)}
          onClose={() => setPreviewTemplate(null)}
          printerId={printer?.id}
          paperSizeId={paperSize?.id}
        />
      )}
    </>
  );
}
